import { useState } from "react";
import { Button } from "../../components/button/button";
import UserService from "../../utilities/services/user-service";
import { User } from "../../models/interfaces/user";
import { useAuthState } from "../../utilities/contexts/auth-state-context";
import { createLogger } from "../../utilities/logger";

const log = createLogger("FixInvalidBirthdatesTool");

const COMPONENT_CLASS = "c-fix-invalid-birthdates-tool";

interface BirthdateRow {
    user: User;
    currentBirthdate: string | null | undefined;
    status?: "pending" | "fixed" | "error";
    error?: string;
}

const FixInvalidBirthdatesTool: React.FC = () => {
    const { state } = useAuthState();
    const [loading, setLoading] = useState(false);
    const [scanned, setScanned] = useState(false);
    const [totalUsers, setTotalUsers] = useState(0);
    const [rows, setRows] = useState<BirthdateRow[]>([]);
    const [applying, setApplying] = useState(false);
    const [appliedCount, setAppliedCount] = useState(0);

    const handleScan = async () => {
        setLoading(true);
        setScanned(false);
        setRows([]);
        setAppliedCount(0);

        try {
            const users = await UserService.getAll();
            setTotalUsers(users.length);

            const badRows: BirthdateRow[] = [];
            for (const user of users) {
                // Only the accidental sentinel values ("", "Invalid Date") are
                // cleaned up. Missing birthdates (undefined/null) are already
                // handled as "not provided" and are not touched.
                if (user.birthdate === "" || user.birthdate === "Invalid Date") {
                    badRows.push({
                        user,
                        currentBirthdate: user.birthdate,
                        status: "pending",
                    });
                }
            }

            setRows(badRows);
            setScanned(true);

            log.info("Invalid birthdates scanned", {
                totalUsers: users.length,
                invalidCount: badRows.length,
            });
        } catch (error) {
            log.error("Failed to scan users for invalid birthdates", { exception: error });
        } finally {
            setLoading(false);
        }
    };

    const handleApply = async () => {
        const toFix = rows.filter((r) => r.status === "pending");
        if (toFix.length === 0) return;

        setApplying(true);
        const CHUNK_SIZE = 50;
        let updated = 0;

        for (let i = 0; i < toFix.length; i += CHUNK_SIZE) {
            const chunk = toFix.slice(i, i + CHUNK_SIZE);
            const results = await Promise.all(chunk.map(async (row) => {
                try {
                    await UserService.updateFields(row.user, { birthdate: null }, state.user ?? null);
                    row.status = "fixed";
                    return true;
                } catch (error) {
                    row.status = "error";
                    row.error = error instanceof Error ? error.message : "Unknown error";
                    log.error("Failed to fix invalid birthdate", { userId: row.user.id, exception: error });
                    return false;
                }
            }));
            updated += results.filter(Boolean).length;
            setAppliedCount(updated);
            setRows([...rows]);
        }

        log.info("Invalid birthdates fixed", {
            fixedCount: updated,
            failedCount: toFix.length - updated,
        });
        setApplying(false);
    };

    const pendingCount = rows.filter((r) => r.status === "pending").length;
    const errorCount = rows.filter((r) => r.status === "error").length;

    return (
        <div className={COMPONENT_CLASS}>
            <h2>Fix Invalid Birthdates</h2>
            <p>
                One-time data fix: some users have an accidental birthdate value of an empty string or
                "Invalid Date", which breaks age calculations (NaN). Scan for these values and fix them
                all to null so they are treated as "not provided". Users without a birthdate that is
                simply missing are not touched.
            </p>

            <div className={`${COMPONENT_CLASS}__actions`}>
                <Button
                    onClick={handleScan}
                    buttonText={loading ? "Scanning..." : "Scan Users"}
                    disabled={loading || applying}
                />
                {scanned && pendingCount > 0 && (
                    <Button
                        onClick={handleApply}
                        buttonText={applying ? `Fixing... ${appliedCount}/${pendingCount}` : `Fix All (${pendingCount})`}
                        disabled={applying}
                    />
                )}
            </div>

            {scanned && (
                <div className={`${COMPONENT_CLASS}__content`}>
                    <p>
                        Scanned {totalUsers} users. {rows.length} invalid birthdate
                        {rows.length === 1 ? "" : "s"} found ({pendingCount} pending, {appliedCount} fixed
                        {errorCount > 0 ? `, ${errorCount} failed` : ""}).
                    </p>

                    {rows.length > 0 ? (
                        <>
                            <label>Invalid Birthdates</label>
                            <table className="light" cellSpacing={0} cellPadding={0}>
                                <thead>
                                    <tr>
                                        <th>User ID</th>
                                        <th>Email</th>
                                        <th>Current Birthdate</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((r, i) => (
                                        <tr key={i}>
                                            <td>{r.user.id}</td>
                                            <td>{r.user.email}</td>
                                            <td>{`"${r.currentBirthdate}"`}</td>
                                            <td>
                                                {r.status === "fixed" && "Fixed (set to null)"}
                                                {r.status === "error" && `Error: ${r.error}`}
                                                {r.status === "pending" && "Pending"}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </>
                    ) : (
                        <p>No invalid birthdates found.</p>
                    )}
                </div>
            )}
        </div>
    );
};

export default FixInvalidBirthdatesTool;
